import * as React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

import Homepage from "./page/Homepage/Homepage";
import Signin from "./page/Sigin/Signin";
import ModuleViewer from "./page/ModuleViewer/ModuleViewer";
import ModuleQuiz from "./page/ModuleQuiz/ModuleQuiz";
import ResetPassword from "./page/ResetPassword/ResetPassword";
import SetupPassword from "./page/SetupPassword/SetupPassword";
import RequireAuth from "./auth/RequireAuth";
import VoiceflowWidget from "./hooks/useVoiceflow";
import ConfirmTicket from "./page/ConfirmTicket/ConfirmTicket";
import VerifyCertificate from "./page/VerifyCertificate/VerifyCertificate";
import { UserProgressProvider } from "./contexts/UserProgressContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { brandingConfig } from "./theme/brandingConfig";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Prevent multiple refetches on window focus
      staleTime: 1000 * 60 * 15, // 15 minutes
    },
  },
});

function AppRoutes({ loading, user }) {
  const location = useLocation();
  const isVerifyPage = location.pathname.startsWith("/verify/");

  return (
    <>
      <UserProgressProvider>
        <Routes>
          <Route path="/login" element={<Signin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route path="/confirm-ticket" element={<ConfirmTicket />} />
          <Route path="/verify/:certificateId" element={<VerifyCertificate />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Homepage />
              </RequireAuth>
            }
          />
          <Route
            path="/module/:moduleId"
            element={
              <RequireAuth>
                <ModuleViewer />
              </RequireAuth>
            }
          />
          <Route
            path="/module/:moduleId/quiz"
            element={
              <RequireAuth>
                <ModuleQuiz />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </UserProgressProvider>

      {!loading && user && !isVerifyPage && <VoiceflowWidget />}
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    document.title = brandingConfig.appTitle;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes loading={loading} user={user} />
      </Router>
    </QueryClientProvider>
  );
}
